import type { OnboardingTunnelStepId } from './onboarding-registry.types';

/**
 * Volet B du plan onboarding v4 (« le pourquoi ») : UNE source pour tout ce qu'on dit d'une étape
 * du tunnel — gain, pourquoi, durée, ce qu'on écrit, ce qu'on ne fait jamais, phrase de victoire.
 * Lue par la démo (écran 8), la progression (16), chaque étape, la fin (18 bis), le récap (18
 * quater) et, au lot 4, les variables de relance `{{parcours:prochaine_gain}}`.
 *
 * Les TEXTES vivent dans `fr-FR.ts`, namespace `PROFESSIONAL_APP_ONBOARDING_TUNNEL_COPY`, sous les
 * clés `<STEP>_<FACET>` (ex. `OFFER_WHY`) : un seul bloc à relire pendant la recette. Ici on ne
 * garde que ce qui n'est pas du texte (durée) et la convention de clés.
 */
export const TUNNEL_COPY_NAMESPACE = 'PROFESSIONAL_APP_ONBOARDING_TUNNEL_COPY';

/** Facettes de texte disponibles pour chaque étape. */
export type TunnelCopyFacet =
  /** Ce que l'étape change pour de vrai, en une ligne (écran 16, fin, relances). */
  | 'GAIN'
  /** La raison de la demande, une ligne sous le titre (B1). */
  | 'WHY'
  /** « En savoir plus » : deux ou trois phrases (B1). */
  | 'WHY_MORE'
  /** Ce qu'on ne fait jamais (B2), affiché sous le pourquoi et avant les permissions. */
  | 'NEVER'
  /** Ce qu'on écrit dans le compte quand l'étape est validée. */
  | 'WRITES'
  /** Phrase de victoire au passage de l'étape (A1) — ICU, valeurs réelles en paramètres. */
  | 'VICTORY'
  /** Phrase d'encouragement fixe affichée pendant l'étape (A2). */
  | 'CHEER';

/** Durée annoncée de chaque étape, en minutes (spec v4, section C : « six étapes, dix minutes »). */
export const TUNNEL_STEP_DURATION_MIN: Record<OnboardingTunnelStepId, number> = {
  offer: 3,
  places_hours: 2,
  share: 1,
  website: 2,
  seo: 1,
  payment: 1,
};

/** Durée totale annoncée du tunnel. */
export const TUNNEL_TOTAL_DURATION_MIN: number = Object.values(TUNNEL_STEP_DURATION_MIN).reduce(
  (sum, minutes): number => sum + minutes,
  0,
);

/** Clé de traduction d'une facette d'étape (`OFFER_WHY`, `PAYMENT_VICTORY`…). */
export const tunnelCopyKey = (stepId: OnboardingTunnelStepId, facet: TunnelCopyFacet): string =>
  `${stepId.toUpperCase()}_${facet}`;

/** Minutes restantes annoncées pour les étapes qui ne sont ni faites ni ignorées. */
export const tunnelRemainingMinutes = (
  steps: ReadonlyArray<{ id: string; status: 'done' | 'skipped' | 'current' | 'todo' }>,
): number =>
  steps
    .filter((step): boolean => step.status === 'current' || step.status === 'todo')
    .reduce((sum, step): number => sum + (TUNNEL_STEP_DURATION_MIN[step.id as OnboardingTunnelStepId] ?? 0), 0);

/**
 * Objectifs de la semaine tant qu'aucune réservation en ligne n'est arrivée (écran 16 ter) :
 * 3 réservations, 1 hors horaires, 1 h gagnée — les mêmes pour tous les secteurs au départ
 * (décision du 29/10/2026), réglables dans l'admin au lot 4 et ajustés par secteur quand le volume
 * le permet. Jamais au-dessus de ce que la moitié des pros atteignent.
 */
export const ONBOARDING_WEEKLY_GOALS_DEFAULT = {
  online_bookings: 3,
  outside_hours: 1,
  minutes_saved: 60,
} as const;

/** Temps gagné estimé : minutes par réservation en ligne (pas d'appel ni de message à gérer). */
export const WEEKLY_MINUTES_PER_ONLINE_BOOKING = 4;
/** Temps gagné estimé : minutes par message automatique envoyé (confirmation, rappel). */
export const WEEKLY_MINUTES_PER_AUTOMATIC_MESSAGE = 2;
